/**
 * OKLCH to sRGB, because a canvas cannot be trusted with `oklch()`.
 *
 * Every colour in `src/styles/tokens.css` is authored in OKLCH, which is right
 * for the app — the browser resolves it, and the fan's single-hue ramp only holds
 * together because lightness and chroma move independently of hue. The share card
 * cannot rely on that. `ctx.fillStyle = 'oklch(...)'` is supported on Chrome 111+,
 * Safari 16.4+ and Firefox 113+, and on anything older the assignment is **not an
 * error** — it is silently ignored, and the shape paints in whatever fill colour
 * was set last. The product's primary traffic arrives inside Instagram and
 * Facebook WebViews, which is exactly where that bites and exactly where nobody
 * would see the exception that never gets thrown.
 *
 * So the card converts, and `card.test.ts` asserts that no `oklch(` string ever
 * reaches a canvas context. The conversion happens once per render, off the hot
 * path, so none of this needs to be fast.
 *
 * The gamut mapping is not optional decoration. Five of the tokens are outside
 * sRGB, and for the gold family the naive fix — clamping each channel into 0..1
 * after conversion — is visibly wrong: `--c-confidence-high` on dark clips to
 * #ffc944 but maps to #ffcd76, a difference of fifty levels of blue. That token
 * is the headline colour at high probability, i.e. precisely the celebratory
 * card someone actually posts. Clipping would make the card disagree with the
 * screenshot beside it in the one case that matters most.
 */

/** A colour as authored in `tokens.css`: lightness 0..1, chroma, hue in degrees. */
export type Oklch = readonly [l: number, c: number, h: number]

export type Rgb = readonly [r: number, g: number, b: number]

/**
 * Just-noticeable difference in OKLab, from the CSS Color 4 gamut-mapping
 * algorithm. Below this, the clipped colour is indistinguishable from the
 * chroma-reduced one and the spec says to prefer the clip.
 */
const JND = 0.02

/** Binary-search resolution on chroma. Well below a perceptible step. */
const EPSILON = 0.0001

function oklchToOklab([l, c, h]: Oklch): [number, number, number] {
  const rad = (h * Math.PI) / 180
  return [l, c * Math.cos(rad), c * Math.sin(rad)]
}

/** OKLab to linear-light sRGB. Coefficients from the OKLab specification. */
function oklabToLinearRgb([l, a, b]: [number, number, number]): [number, number, number] {
  const lRoot = l + 0.3963377774 * a + 0.2158037573 * b
  const mRoot = l - 0.1055613458 * a - 0.0638541728 * b
  const sRoot = l - 0.0894841775 * a - 1.291485548 * b

  const lCubed = lRoot * lRoot * lRoot
  const mCubed = mRoot * mRoot * mRoot
  const sCubed = sRoot * sRoot * sRoot

  return [
    4.0767416621 * lCubed - 3.3077115913 * mCubed + 0.2309699292 * sCubed,
    -1.2684380046 * lCubed + 2.6097574011 * mCubed - 0.3413193965 * sCubed,
    -0.0041960863 * lCubed - 0.7034186147 * mCubed + 1.707614701 * sCubed,
  ]
}

/** The sRGB transfer function. Input and output are both 0..1. */
function gammaEncode(channel: number): number {
  const sign = channel < 0 ? -1 : 1
  const magnitude = Math.abs(channel)
  return magnitude <= 0.0031308
    ? channel * 12.92
    : sign * (1.055 * Math.pow(magnitude, 1 / 2.4) - 0.055)
}

/** True while every channel sits inside 0..1, with a hair of tolerance. */
function inGamut([r, g, b]: [number, number, number]): boolean {
  const tolerance = 1e-6
  return (
    r >= -tolerance &&
    r <= 1 + tolerance &&
    g >= -tolerance &&
    g <= 1 + tolerance &&
    b >= -tolerance &&
    b <= 1 + tolerance
  )
}

function clipToGamut([r, g, b]: [number, number, number]): [number, number, number] {
  return [
    Math.min(1, Math.max(0, r)),
    Math.min(1, Math.max(0, g)),
    Math.min(1, Math.max(0, b)),
  ]
}

/** Euclidean distance in OKLab — the spec's deltaEOK. */
function deltaEOk(a: [number, number, number], b: [number, number, number]): number {
  const dl = a[0] - b[0]
  const da = a[1] - b[1]
  const db = a[2] - b[2]
  return Math.sqrt(dl * dl + da * da + db * db)
}

/** Gamma-encoded sRGB back to OKLab, so the clipped candidate can be compared. */
function gammaEncodedRgbToOklab([r, g, b]: [number, number, number]): [
  number,
  number,
  number,
] {
  const linear = (channel: number): number => {
    const sign = channel < 0 ? -1 : 1
    const magnitude = Math.abs(channel)
    return magnitude <= 0.04045
      ? channel / 12.92
      : sign * Math.pow((magnitude + 0.055) / 1.055, 2.4)
  }
  const lr = linear(r)
  const lg = linear(g)
  const lb = linear(b)

  const lRoot = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const mRoot = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const sRoot = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)

  return [
    0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot,
    1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot,
    0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot,
  ]
}

/**
 * OKLCH to sRGB channels in 0..255, gamut-mapped per CSS Color 4.
 *
 * Out-of-gamut colours have their chroma reduced by binary search until they fit,
 * except that the search stops early once a plain clip of the candidate is within
 * a just-noticeable difference of it — which preserves more of the original
 * chroma than reducing all the way would.
 */
export function oklchToRgb(colour: Oklch): Rgb {
  const [l, c, h] = colour

  // The spec's two shortcuts. Outside these, cube roots of near-zero lightness
  // make the search behave badly for no benefit.
  if (l >= 1) return [255, 255, 255]
  if (l <= 0) return [0, 0, 0]

  const direct = oklabToLinearRgb(oklchToOklab(colour)).map(gammaEncode) as [
    number,
    number,
    number,
  ]
  if (inGamut(direct)) return toBytes(direct)

  let low = 0
  let high = c
  let best = clipToGamut(direct)

  while (high - low > EPSILON) {
    const mid = (low + high) / 2
    const candidate = oklabToLinearRgb(oklchToOklab([l, mid, h])).map(gammaEncode) as [
      number,
      number,
      number,
    ]

    if (inGamut(candidate)) {
      best = candidate
      low = mid
      continue
    }

    const clipped = clipToGamut(candidate)
    if (
      deltaEOk(gammaEncodedRgbToOklab(clipped), gammaEncodedRgbToOklab(candidate)) < JND
    ) {
      best = clipped
      break
    }
    high = mid
  }

  return toBytes(best)
}

function toBytes([r, g, b]: [number, number, number]): Rgb {
  return [
    Math.round(Math.min(1, Math.max(0, r)) * 255),
    Math.round(Math.min(1, Math.max(0, g)) * 255),
    Math.round(Math.min(1, Math.max(0, b)) * 255),
  ]
}

/** `#rrggbb`. The form to hand a canvas for an opaque fill or stroke. */
export function oklchToHex(colour: Oklch): string {
  const [r, g, b] = oklchToRgb(colour)
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * `rgba(r, g, b, a)`.
 *
 * Canvas gradients have no `stop-opacity`, so every alpha the SVG chart expresses
 * as a separate attribute has to be baked into the colour string here.
 */
export function oklchToRgba(colour: Oklch, alpha: number): string {
  const [r, g, b] = oklchToRgb(colour)
  return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`
}
